import { getSelectedSingleDayDate } from 'lib/experiences/experienceUtils'
import useExperienceSearch from 'hooks/Experiences/useExperienceSearch'
import { fetchExperiencesById, getMinExperienceOffers } from 'actions/ExperienceActions'
import { fillArray, nonNullable, take } from 'lib/array/arrayUtils'
import { EXPERIENCE_SEARCH_STREAM_PAGE_SIZE } from 'api/experienceSearchStream'
import { rem } from 'polished'
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { mediaQueryUp } from 'components/utils/breakpoint'
import ExperienceCompactSearchTile from '../ExperienceTiles/ExperienceCompactSearchTile'
import ExperienceCompactSearchTileSkeleton from '../ExperienceTiles/ExperienceCompactSearchTileSkeleton'
import { viewItemListExperiences } from 'analytics/eventDefinitions'
import { addGTMEvent } from 'api/googleTagManager'
import OfferListInfiniteScroll from 'components/OfferList/OfferListInfiniteScroll'
import Group from 'components/utils/Group'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'

const HTTP_SEARCH_LIST_MAX_OFFERS = EXPERIENCE_SEARCH_STREAM_PAGE_SIZE * 10

const ListGrid = styled.div`
  display: flex;
  flex-direction: column;
  min-height: ${rem(200)};
  position: relative;
  gap: ${rem(16)};

  ${mediaQueryUp.tablet} {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(${rem(300)}, 1fr));
    gap: ${rem(36)} ${rem(16)};
  }
`

interface SourceList {
  ids: Array<string>
  listKey?: string
  fetching?: boolean
  searchId?: string
  streamEnded?: boolean
  socketListenersStatus?: App.SocketListenersStatus
}

interface Props {
  placeId: string;
  sortBy?: Experiences.SortTypes;
  categoryCodes?: Array<number>;
  priceLte?: number;
  priceGte?: number;
  campaigns?: Array<string>;
  excludeIds?: Array<string>;
  luxPlusFeatures?: Array<App.LuxPlusFeatureFilter>;
  collections?: Array<string>;
  from?: string;
  to?: string;
  minRating?: number;
  leExclusive?: boolean;
  travelStyles?: Array<Experiences.TravelStyleFilter>;
  featured?: Array<Experiences.FeaturedFilter>;
  timeSlots?: Array<Experiences.TimeSlotFilter>;
  bounds?: string;
  sourceList?: SourceList;
}

function ExperienceDenseSearchList(props: Props) {
  const {
    placeId,
    sortBy,
    categoryCodes,
    priceGte,
    priceLte,
    campaigns,
    excludeIds,
    luxPlusFeatures,
    collections,
    from,
    to,
    minRating,
    leExclusive,
    travelStyles,
    featured,
    timeSlots,
    bounds,
    sourceList,
  } = props
  const dispatch = useAppDispatch()
  const [page, setPage] = useState<number>(1)
  const useSourceList = !!sourceList

  useEffect(() => {
    setPage(1)
  }, [
    placeId,
    sortBy,
    categoryCodes,
    priceGte,
    priceLte,
    campaigns,
    luxPlusFeatures,
    collections,
    from,
    to,
    minRating,
    leExclusive,
    travelStyles,
    featured,
    timeSlots,
    bounds,
  ])

  const limit = useSourceList
    ? page * EXPERIENCE_SEARCH_STREAM_PAGE_SIZE
    : Math.min(page * EXPERIENCE_SEARCH_STREAM_PAGE_SIZE, HTTP_SEARCH_LIST_MAX_OFFERS)
  const [fetchedList, fetchedExperiences] = useExperienceSearch(
    { placeId, limit, sortBy, excludeIds, campaigns, from, to, enabled: !useSourceList },
    {
      categoryCodes,
      priceGte,
      priceLte,
      luxPlusFeatures,
      collections,
      minRating,
      leExclusive,
      travelStyles,
      featured,
      timeSlots,
      bounds,
    },
  )
  const list = sourceList ?? fetchedList
  const experiencesById = useAppSelector((state) => state.experience.experiences)
  const sourceExperienceItems = useMemo(() => {
    if (!sourceList) return fetchedExperiences
    return take(sourceList.ids, limit).map(id => experiencesById[id])
  }, [sourceList, fetchedExperiences, limit, experiencesById])
  const experiences = sourceExperienceItems
  const streamCanExtend = useSourceList && sourceList.socketListenersStatus === 'listening' && !sourceList.streamEnded
  const hasMore = !!list && (experiences.length < list.ids.length || !!list.fetching || !!streamCanExtend)

  useEffect(() => {
    if (!sourceList?.ids.length) return
    dispatch(fetchExperiencesById(...take(sourceList.ids, limit)))
  }, [dispatch, limit, sourceList?.ids])

  useEffect(() => {
    if (!sourceList?.listKey) return
    dispatch(getMinExperienceOffers({ listKey: sourceList.listKey, minOffers: limit }))
  }, [dispatch, limit, sourceList?.listKey, sourceList?.fetching, sourceList?.socketListenersStatus])

  const sortedExperiences = useMemo(() => {
    if (sortBy !== 'bestValue') return experiences
    return [...experiences].sort(
      (a, b) => (b?.discount ?? 0) - (a?.discount ?? 0),
    )
  }, [experiences, sortBy])

  useEffect(() => {
    if (useSourceList) return
    if (!experiences?.length) return
    const event = viewItemListExperiences(nonNullable(experiences))
    if (event) addGTMEvent(event)
  }, [experiences, useSourceList])

  const handleLoadMore = useCallback(() => setPage((prev) => prev + 1), [])
  const date = getSelectedSingleDayDate({ checkIn: from, checkOut: to })
  const showInitialSkeletons = (!list || list.fetching) && sortedExperiences.length === 0

  return (
    <div>
      <ListGrid>
        {showInitialSkeletons &&
          fillArray(8).map((id) => (
            <ExperienceCompactSearchTileSkeleton key={id} />
          ))}
        {sortedExperiences.map((exp, idx) => (
          <Fragment key={`${exp?.id ?? 'pending'}-${idx}`}>
            {!exp && <ExperienceCompactSearchTileSkeleton />}
            {exp && (
              <ExperienceCompactSearchTile
                experience={exp}
                position={idx}
                date={date}
                listKey={list?.listKey}
              />
            )}
          </Fragment>
        ))}
      </ListGrid>
      <Group
        direction="horizontal"
        verticalAlign="center"
        horizontalAlign="center"
      >
        <OfferListInfiniteScroll
          onLoadMore={handleLoadMore}
          isLoading={!!list?.fetching}
          hasMore={hasMore}
        />
      </Group>
    </div>
  )
}

export default ExperienceDenseSearchList
